import { Component, ReactNode, ErrorInfo } from 'react';

import RouteErrorPageContent from './RouteErrorPageContent';
import { getIsChunkError, handleRefresh, removeHasReloaded } from './helpers';

interface ChunkErrorBoundaryProps {
  children?: ReactNode;
}

interface ChunkErrorBoundaryState {
  error: Error | null;
}

class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  public state: ChunkErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    // Catch ALL errors and save them to state.
    // This stops React from infinitely attempting to re-render the broken children.
    return { error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (this.isChunkError(error)) {
      handleRefresh();
    }
  }

  public componentDidMount(): void {
    if (!this.state.error) {
      removeHasReloaded();
    }
  }

  // Extracted logic to keep code DRY
  private isChunkError(error: Error): boolean {
    return getIsChunkError(error);
  }

  public render(): ReactNode {
    const { error } = this.state;

    if (error) {
      if (this.isChunkError(error)) {
        return <RouteErrorPageContent error={error} isChunkError={true} />;
      }

      // If it's a standard error, rethrow it so a parent ErrorBoundary can catch it.
      // This allows you to handle standard app crashes normally.
      throw error;
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
